/**
 * Page de réclamation d'un accès offert (handoff acheteur → enfant).
 *
 * URL : /offert/<token> — accessible sans compte.
 * Non connecté → connexion/inscription OTP inline, puis réclamation.
 * Connecté    → bouton « Récupérer mon accès », qui appelle claim-purchase-grant.
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CompassLogo } from '@jeraisonne/ui';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { supabase, supabaseUrl, supabaseAnonKey } from '@/lib/supabase';

const CLAIM_FAILED = 'La récupération a échoué. Réessaie dans un instant.';

const ERROR_LABELS: Record<string, string> = {
  grant_not_found: "Ce lien n'existe pas ou a été supprimé.",
  grant_not_pending: 'Cet accès a déjà été récupéré.',
  grant_expired: "Ce lien a expiré. Demande à l'acheteur d'en générer un nouveau.",
  cannot_claim_own_grant: 'Tu ne peux pas récupérer ton propre accès — il est déjà actif.',
  claim_failed: CLAIM_FAILED,
};

type ClaimState =
  | { status: 'idle' }
  | { status: 'claiming' }
  | { status: 'done' }
  | { status: 'error'; message: string };

function getToken(): string {
  return decodeURIComponent(window.location.pathname.split('/').filter(Boolean).pop() ?? '');
}

export function OffertPage() {
  const token = getToken();
  const { session } = useAuth();
  const [state, setState] = useState<ClaimState>({ status: 'idle' });

  async function handleClaim() {
    setState({ status: 'claiming' });
    const {
      data: { session: s },
    } = await supabase.auth.getSession();
    if (!s) {
      setState({ status: 'error', message: 'Connecte-toi pour récupérer ton accès.' });
      return;
    }
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/claim-purchase-grant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${s.access_token}`,
        },
        body: JSON.stringify({ token }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        setState({ status: 'done' });
        window.setTimeout(() => (window.location.href = '/'), 1800);
        return;
      }
      setState({ status: 'error', message: ERROR_LABELS[data.error ?? ''] ?? CLAIM_FAILED });
    } catch {
      setState({ status: 'error', message: CLAIM_FAILED });
    }
  }

  return (
    <div className="min-h-dvh bg-transparent">
      <header className="flex justify-center px-6 py-8">
        <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
      </header>

      <main className="mx-auto flex w-full max-w-md flex-col px-6 pb-16 pt-4 sm:pt-12">
        <div className="border-primary-200 bg-primary-50 mb-6 rounded-2xl border p-5 text-center">
          <p className="text-3xl">🎁</p>
          <p className="text-primary-700 mt-2 font-serif text-xl font-semibold">
            Un accès t&apos;a été offert
          </p>
          <p className="text-ink-600 mt-1 text-sm">
            {session
              ? 'Récupère-le sur ton compte en un clic.'
              : 'Connecte-toi ou crée ton compte pour en profiter.'}
          </p>
        </div>

        <Card className="border-ink-100/60 shadow-lg">
          {!session ? (
            <OtpAuthForm
              initialMode="signup"
              renderHeader={() => (
                <>
                  <CardTitle>Crée ton compte</CardTitle>
                  <CardDescription>Ton accès sera activé juste après ta connexion.</CardDescription>
                </>
              )}
            />
          ) : (
            <CardHeader>
              <div className="space-y-5 py-2 text-center">
                {state.status === 'done' ? (
                  <>
                    <p className="text-4xl">✅</p>
                    <CardTitle>Accès activé !</CardTitle>
                    <CardDescription>On t&apos;emmène à ton tableau de bord…</CardDescription>
                  </>
                ) : (
                  <>
                    <CardTitle>Récupérer mon accès</CardTitle>
                    {state.status === 'error' && (
                      <CardDescription className="text-error-600">{state.message}</CardDescription>
                    )}
                    <button
                      type="button"
                      onClick={handleClaim}
                      disabled={state.status === 'claiming'}
                      className="bg-primary-500 hover:bg-primary-600 min-h-11 w-full rounded-xl py-3 text-sm font-bold text-white shadow transition active:scale-[0.98] disabled:opacity-60"
                    >
                      {state.status === 'claiming' ? 'Activation…' : 'Récupérer mon accès offert'}
                    </button>
                  </>
                )}
              </div>
            </CardHeader>
          )}
        </Card>
      </main>
    </div>
  );
}
